import { useMarkerVisibilityView } from 'hooks/map/useMarkerVisibilityView';
import { mapGraphQLMarkers } from 'libs/utils/mapping/worldGrahQLMaps';
import { CustomMapPage, CustomMapPageProps } from 'pages/custom/shared/CustomMapPage';
import { useContext, useMemo, useRef, useState } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { MapRef } from 'react-map-gl';
import { CtxView } from 'session/ctxView';

const DEFAULT_ZOOM = 17.39;

export const NorboatMapWrapper = (props: CustomMapPageProps): JSX.Element => {
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);
    const ref = useRef<MapRef>(null);
    const ctxView = useContext(CtxView);
    const markerVisibility = useMarkerVisibilityView();

    const getLevel = () => {
        if (zoom < 16.1) {
            return 1;
        }
        if (zoom < 18.65) {
            return 2;
        }
        return 3;
    };

    const level = getLevel();

    const markers = useMemo(
        () =>
            mapGraphQLMarkers(
                (ctxView.allMarkers ?? []).map((marker) => ({
                    ...marker,
                    selectable: markerVisibility.isMarkerSelectableInView(marker),
                    visible: markerVisibility.isMarkerVisibleInViewMap(marker)
                }))
            ),
        [ctxView.allMarkers]
    );

    const makersLvl1 = useMemo(
        () =>
            markers
                .filter((e) => e.id === '0.7923782822380445' || e.elementType === 'v2/wall')
                .map<IWorldMarker>((e) =>
                    e.elementType === 'v2/wall'
                        ? { ...e, elementData: { ...e.elementData, label: '' } }
                        : e
                ),
        [markers]
    );

    const makersLvl2 = useMemo(
        () =>
            markers
                .filter((e) => e.id !== '0.7923782822380445')
                .map<IWorldMarker>((e) =>
                    e.elementType === 'v2/wall'
                        ? { ...e, elementData: { ...e.elementData, label: '' } }
                        : e
                ),
        [markers]
    );

    const makersLvl3 = useMemo(
        () => markers.filter((e) => e.id !== '0.7923782822380445'),
        [markers]
    );

    const final = useMemo(() => {
        if (level === 1) {
            return makersLvl1;
        }
        if (level === 2) {
            return makersLvl2;
        }
        return makersLvl3;
    }, [level, ctxView.allMarkers]);

    return (
        <CustomMapPage
            {...props}
            overrideMapMarkers={final}
            mapProps={{
                ...props.mapProps
            }}
            onMapLoaded={(mapRef) => {
                const map = mapRef?.getMap();

                ref.current = mapRef;

                setZoom(map?.getZoom() ?? DEFAULT_ZOOM);

                map.on('zoom', (e) => {
                    const z = e?.target?.getZoom();
                    setZoom(z);
                });

                props.onMapLoaded?.(mapRef);
            }}
            onMarkerSelected={(markerId) => {
                return props?.onMarkerSelected?.(markerId);
            }}
        />
    );
};
